/* Ask / Instruct — one form, two homes.

   Embedded: lives on the project page (and document page) so the user can
   see they are talking to THIS project only.
   Overlay: ⌘K / navbar trigger. Inside a project, ⌘K focuses the embedded
   panel instead of opening a floating palette. Outside a project it still
   opens the overlay (org guide / knowledge). */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import firebase, { db } from '../firebaseConfig';
import { useOrg } from '../data/orgs';
import { useEntityList } from '../data/entities';
import { useDealPowers } from '../data/permissions';
import { aiDocAgent } from '../data/legalApi';
import { openGuide } from './GuidePanel';
import { examplesForPath } from '../data/guideContent';
import AddDocumentPortal from './AddDocumentPortal';
import './CommandBar.css';

const API = process.env.REACT_APP_UPLOAD_API || '';

export function instructionFromThread(thread, fallback = '') {
  const turns = (Array.isArray(thread) ? thread : []).slice(-10);
  if (!turns.length) return String(fallback || '').trim();
  const lines = turns.map((m) => {
    const role = m.role === 'user' ? 'Q' : 'A';
    const text = String(m.content || m.text || '').trim();
    return text ? `${role}: ${text}` : '';
  }).filter(Boolean);
  if (!lines.length) return String(fallback || '').trim();
  return (
    'Apply the conclusions of this counsel conversation as a precision edit to the draft. ' +
    'Prefer the latest recommendation. File reviewable ops (they become Word tracked changes on export).\n\n' +
    lines.join('\n\n')
  );
}

export function openCommandBar(detail) {
  if (document.getElementById('project-command')) {
    window.dispatchEvent(new CustomEvent('fl:project-cmdk', { detail: detail || {} }));
    return;
  }
  // Overlay path: detail { verb, instruction } prefills the palette (used by
  // the Home dashboard's ask box and AI action rail).
  window.dispatchEvent(new CustomEvent('fl:cmdk', { detail: detail || {} }));
}

async function authHeaders() {
  const user = firebase.auth().currentUser;
  const token = user ? await user.getIdToken() : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function Elapsed({ since }) {
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  return <span className="cb-elapsed">{Math.round((Date.now() - since) / 1000)}s</span>;
}

const ASK_SUGGESTIONS_DEAL = [
  'What is still open, and what is each party’s stance?',
  'Explain the indemnification point — what does the current draft actually say?',
  'Is our register position consistent with the current draft?',
  'Summarize what changed in the latest version',
  'Produce a market study on whether the post-employment non-compete is in line with market, then recommend edits',
  'Screen the restrictive covenants for Minnesota enforceability and propose compliant language',
  'Which agreement owns members, capital, and governance — HoldCo or OpCo?',
];
const ASK_SUGGESTIONS_ORG = [
  'How do I ask, instruct, or add a document inside a project?',
  'How do I upload a new draft from the counterparty?',
  'What is the difference between clean and redline views?',
  'How do I export a package — clean copy, redline, and open points in one download?',
  'How do I file correspondence against the register, or verify the draft reflects it?',
  'How do I share a package with an expiring link, or export on letterhead?',
];
const DO_SUGGESTIONS = [
  'In §12.5 keep the AI-output indemnity carve-out, and add that Customer validates outputs before commercial use',
  'Propose middle-ground liability-cap language at $2,000,000 and apply it in Section 11',
  'Replace every reference to [old name] with [new name]',
  'Make the post-employment non-compete compliant with Minnesota law and file a reviewable edit',
  'Add a Change of Control definition to the assignment section',
  'Paste an email thread here — the agent extracts the drafting instructions from it',
  'Target "All documents" to route one instruction across the project — e.g. conform the SOW to the MSA\u2019s IP clause',
];

// Mirror of backend/shared/counselAsk.js + jurisdictionScreen.js
function looksLikeComplianceEdit(text) {
  const t = String(text || '').trim();
  if (!t) return false;
  const edit = /\b(make|amend|revise|rewrite|redraft|fix|conform|incorporat|apply|propose|add|change|strike)\b/i;
  const compliance = /\b(compliant|compliance|enforceab|non[- ]compete|restrictive covenant|jurisdiction|minnesota|california|delaware)\b/i;
  return edit.test(t) && compliance.test(t);
}
function looksLikeCounselAsk(text) {
  const t = String(text || '').trim();
  if (!t) return false;
  if (looksLikeComplianceEdit(t)) return false;
  if (/\?\s*$/.test(t)) return true;
  const edit = /\b(add|insert|delete|remove|replace|rewrite|redraft|change|amend|strike|move|renumber|apply|file a|draft a|create a)\b/i;
  const ask = /\b(whether|is this|is the|are these|explain|summarize|compare|in[- ]line with|inline with|what does|why (is|does|not)|who owns|provide whether)\b/i;
  return ask.test(t) && !edit.test(t);
}
// Mirror of backend/api/docAgent.js looksLikePastedMail — warns the user that
// a pasted email thread gets distilled into drafting instructions server-side.
function looksLikePastedMail(text) {
  const t = String(text || '');
  if (t.length > 2500) return true;
  let signals = 0;
  if (/(^|\n)\s*(from|sent|to|cc|subject)\s*:\s.+/i.test(t)) signals += 1;
  if (/\bwrote:\s*$/m.test(t) || /^On .{8,90}(wrote|<)/m.test(t)) signals += 1;
  if (/skip to content|unsubscribe|forwarded message|caution:.*external/i.test(t)) signals += 1;
  if (/^\s*>/m.test(t)) signals += 1;
  if (/\bto (me|all)\b.*\(\d+\s+(minutes?|hours?|days?)\s+ago\)/i.test(t)) signals += 1;
  return signals >= 2;
}

// Live view of a queued cross-document run — the server routes the
// instruction and files per-document proposals in the background, streaming
// results into the agentRuns doc this component watches (the HTTP request
// returns immediately; a routed run outlives the gateway timeout).
function DealEditRun({ runPath, question, dealId, onClose }) {
  const [run, setRun] = useState(null);
  useEffect(() => {
    if (!runPath) return undefined;
    const unsub = db.doc(runPath).onSnapshot(
      (snap) => setRun(snap.exists ? snap.data() : null),
      () => setRun({ status: 'error', error: 'could not follow the run' })
    );
    return () => unsub();
  }, [runPath]);

  return (
    <div className="cb-proposal">
      {question && <div className="cb-asked">Instruction: {question}</div>}
      {(!run || run.status === 'running') && (
        <div className="cb-proposal-head">
          Routing across the project — planning targets and filing proposals… <Elapsed since={run?.createdAt || Date.now()} />
        </div>
      )}
      {run?.status === 'error' && <div className="cb-proposal-sum">Run failed — {run.error}</div>}
      {run?.status === 'done' && (
        <>
          <div className="cb-proposal-head">
            Routed across {run.targets.length} document{run.targets.length === 1 ? '' : 's'}
          </div>
          <div className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
            {run.targets.map((t) => (
              <div key={t.docId} className="cb-proposal-sum">
                <strong>{t.title}:</strong>{' '}
                {t.error
                  ? `failed — ${t.error}`
                  : t.alreadyDone
                    ? `already in the draft${t.version ? ` (v${t.version})` : ''}`
                    : `proposal filed — ${t.opCount || 0} change${t.opCount === 1 ? '' : 's'} awaiting review`}
                {!t.error && !t.alreadyDone && (
                  <>
                    {' '}
                    <Link to={`/deals/${dealId}/docs/${t.docId}`} onClick={() => onClose?.()}>Review</Link>
                  </>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function CommandPanel({
  embedded = false,
  orgId: orgIdProp,
  dealId: dealIdProp,
  dealName = '',
  deal = null,
  participant = null,
  defaultDocId = '',
  initial = null, // { verb, instruction } — prefill from openCommandBar(detail)
  collapsible = false, // embedded only: start as a one-line pill so the page's tabs stay above the fold
  onClose,
}) {
  const [verb, setVerb] = useState(initial?.verb === 'do' ? 'do' : 'ask');
  const [input, setInput] = useState(initial?.instruction || '');
  const [expanded, setExpanded] = useState(!collapsible);
  const [busy, setBusy] = useState(null);
  const [result, setResult] = useState(null);
  const [targetDocId, setTargetDocId] = useState('');
  const [thread, setThread] = useState([]); // { role, content } — Ask follow-ups stay in-project
  const inputRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { orgId: orgFromCtx } = useOrg() || {};

  const orgId = orgIdProp || orgFromCtx;
  const dealId = dealIdProp || (location.pathname.match(/^\/deals\/([^/]+)/) || [])[1] || null;
  const routeDocId = defaultDocId || (location.pathname.match(/^\/deals\/[^/]+\/docs\/([^/]+)/) || [])[1] || '';
  const dealScoped = !!(dealId && orgId);
  const { can } = useDealPowers(dealScoped ? dealId : null);
  const canInstruct = dealScoped && can('agent');
  const canUpload = embedded && dealScoped && can('upload') && !!deal;

  const scope = dealScoped ? { org: orgId, deal: dealId } : null;
  const { docs } = useEntityList('dealDoc', scope, { enabled: !!scope });

  const targets = useMemo(
    () => (docs || []).filter((d) => d.status === 'ready' && d.format !== 'pasted'),
    [docs]
  );
  const fallbackTarget = useMemo(() => {
    if (routeDocId && targets.some((d) => d.id === routeDocId)) return routeDocId;
    const primary = targets.find((d) => d.kind === 'primary') || targets[0];
    return primary?.id || '';
  }, [routeDocId, targets]);
  // Re-sync to the route's document when the user navigates; otherwise keep
  // their pick. `targets` is a live list — in a busy room the newest-ready
  // doc changes every few seconds, and snapping the select back to it made
  // the picker look stuck on one document.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setTargetDocId(fallbackTarget); }, [routeDocId, dealId]);
  useEffect(() => {
    setTargetDocId((cur) => (cur === '@deal' || targets.some((d) => d.id === cur) ? cur : fallbackTarget));
  }, [fallbackTarget, targets]);
  useEffect(() => {
    if (!dealId) { setThread([]); setResult(null); return; }
    try {
      const raw = localStorage.getItem(`fl-ask-thread:${dealId}`);
      setThread(raw ? JSON.parse(raw) : []);
    } catch { setThread([]); }
    setResult(null);
  }, [dealId]);
  useEffect(() => {
    if (!dealId) return;
    try { localStorage.setItem(`fl-ask-thread:${dealId}`, JSON.stringify(thread.slice(-12))); } catch { /* nicety */ }
  }, [dealId, thread]);

  useEffect(() => {
    if (!embedded) return undefined;
    const onFocus = (e) => {
      setExpanded(true);
      const el = document.getElementById('project-command');
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (e?.detail?.verb === 'do') {
        setVerb('do');
        if (e.detail.instruction) setInput(e.detail.instruction);
      }
      if (e?.detail?.verb === 'ask') {
        setVerb('ask');
        if (e.detail.instruction) setInput(e.detail.instruction);
      }
      setTimeout(() => inputRef.current?.focus(), 80);
    };
    window.addEventListener('fl:project-cmdk', onFocus);
    return () => window.removeEventListener('fl:project-cmdk', onFocus);
  }, [embedded]);

  async function submit(e) {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setResult(null);
    setBusy({ since: Date.now() });
    try {
      const headers = { 'Content-Type': 'application/json', ...(await authHeaders()) };
      if (verb === 'ask') {
        if (dealScoped) {
          const history = thread.slice(-8).map((m) => ({
            role: m.role === 'user' ? 'user' : 'assistant',
            content: m.content,
          }));
          const res = await fetch(`${API}/legal/ask`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ orgId, dealId, question: text, history, scope: 'project' }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
          setThread((t) => [...t, { role: 'user', content: text }, { role: 'assistant', content: data.answer }].slice(-12));
          setResult({
            kind: 'answer',
            question: text,
            text: data.answer,
            sources: data.sources || [],
            studyDocId: data.studyDocId || '',
            studyTitle: data.studyTitle || '',
            recommendations: data.recommendations || [],
          });
        } else {
          const res = await fetch(`${API}/voice/tool`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ tool: 'ask_knowledge', params: { question: text } }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
          setResult({ kind: 'answer', question: text, text: data.result.text, citations: data.result.citations || [] });
        }
      } else {
        if (!targetDocId) throw new Error('Pick a document to instruct the agent on');
        const r = await aiDocAgent({
          orgId, dealId, docId: targetDocId, instruction: text, mode: 'precision',
          history: thread.slice(-8).map((m) => ({ role: m.role === 'user' ? 'user' : 'assistant', content: m.content })),
        });
        const doc = targets.find((d) => d.id === targetDocId);
        if (r.dealEdit) {
          // Queued run — results stream into the agentRuns doc (watched below).
          setResult({ kind: 'dealedit', question: text, runPath: r.runPath });
        } else if (r.counsel) {
          setThread((t) => [...t, { role: 'user', content: text }, { role: 'assistant', content: r.answer || r.summary }].slice(-12));
          setResult({
            kind: 'answer',
            question: text,
            text: r.answer || r.summary,
            sources: r.sources || [],
            viaInstruct: true,
            studyDocId: r.studyDocId || '',
            studyTitle: r.studyTitle || '',
            recommendations: r.recommendations || [],
          });
        } else if (r.alreadyDone) {
          setResult({
            kind: 'alreadydone',
            question: text,
            text: r.summary,
            evidence: r.evidence || '',
            version: r.version,
            docTitle: r.docTitle || doc?.title || 'document',
          });
        } else if (r.misplaced) {
          const belong = String(r.belongsIn || '').toLowerCase();
          const owner = belong
            ? targets.find((d) => d.title && d.title.toLowerCase() === belong)
              || targets.find((d) => d.title && belong.includes(d.title.toLowerCase()))
              || targets.find((d) => d.title && d.title.toLowerCase().includes(belong))
            : null;
          setResult({
            kind: 'misplaced',
            question: text,
            text: r.summary,
            belongsIn: r.belongsIn,
            belongsInDocId: owner?.id || '',
            docTitle: r.docTitle || doc?.title || 'document',
          });
        } else {
          setResult({
            kind: 'proposal',
            question: text,
            summary: r.summary,
            opCount: (r.ops || []).length,
            docId: targetDocId,
            docTitle: r.docTitle || doc?.title || 'document',
            distilled: r.distilledInstruction || '',
            skipped: r.skippedAlready || [],
          });
        }
      }
      setInput('');
    } catch (err) {
      setResult({ kind: 'error', text: err.message });
    } finally {
      setBusy(null);
    }
  }

  function goReview() {
    onClose?.();
    navigate(`/deals/${dealId}/docs/${result.docId}`);
  }

  if (!API) return null;

  // Suggestion chips come from the guide's per-topic examples for this route
  // (one source of truth with the GuidePanel's Try-it chips); the hard-coded
  // lists remain the fallback where the route has no matching guide topics.
  const guideExamples = examplesForPath(location.pathname, verb === 'do' ? 'do' : 'ask');
  const suggestions = guideExamples.length >= 3
    ? guideExamples.slice(0, 7)
    : verb === 'do' ? DO_SUGGESTIONS : dealScoped ? ASK_SUGGESTIONS_DEAL : ASK_SUGGESTIONS_ORG;
  const wrapClass = embedded ? 'cb-embedded cs-card' : 'cb-modal';

  // Collapsed pill — one line, so the page's own navigation stays above the
  // fold. Expands on click or ⌘K (the fl:project-cmdk listener above). Keeps
  // the #project-command id so openCommandBar() and the tour still find it.
  if (embedded && collapsible && !expanded) {
    return (
      <button
        id="project-command"
        data-tour="project-ask"
        type="button"
        className="cb-pill"
        onClick={() => {
          setExpanded(true);
          setTimeout(() => inputRef.current?.focus(), 80);
        }}
        aria-label={`Ask or instruct ${dealName || 'this project'}`}
      >
        <span className="cb-pill-dot" aria-hidden="true">✦</span>
        <span className="cb-pill-text">
          Ask this project, instruct the drafting agent, or add a document…
        </span>
        <kbd className="cb-pill-kbd">⌘K</kbd>
      </button>
    );
  }

  return (
    <div
      id={embedded ? 'project-command' : undefined}
      data-tour={embedded ? 'project-ask' : undefined}
      className={wrapClass}
      role={embedded ? 'region' : 'dialog'}
      aria-label={embedded ? `Ask or instruct ${dealName || 'this project'}` : 'Command bar'}
    >
      {embedded && (
        <div className="cb-project-banner">
          <div className="u-split" style={{ alignItems: 'baseline' }}>
            <strong>Ask, instruct & add to this project</strong>
            {collapsible && (
              <button
                type="button"
                className="cs-btn cs-btn-ghost cs-btn-sm"
                onClick={() => setExpanded(false)}
                title="Collapse back to one line"
              >
                Collapse ▴
              </button>
            )}
          </div>
          <span>
            {dealName ? `Stays inside “${dealName}”. ` : 'Stays inside this project. '}
            Questions, edits, and new documents do not leave these files.
          </span>
        </div>
      )}
      <div className="cb-verbs">
        <button
          type="button"
          className={`cb-verb ${verb === 'ask' ? 'cb-verb-on' : ''}`}
          onClick={() => setVerb('ask')}
        >
          Ask
        </button>
        <button
          type="button"
          className={`cb-verb ${verb === 'do' ? 'cb-verb-on' : ''}`}
          onClick={() => setVerb('do')}
          disabled={!canInstruct}
          title={
            !dealScoped
              ? 'Open a project to instruct the drafting agent'
              : !canInstruct
                ? 'Requires agent power on this project'
                : 'Instruct the drafting agent — it proposes changes for your review'
          }
        >
          Instruct
        </button>
        {canUpload && (
          <button
            type="button"
            className={`cb-verb ${verb === 'add' ? 'cb-verb-on' : ''}`}
            onClick={() => setVerb('add')}
            title="Add a document to this project — it is ingested, graphed, and indexed here"
          >
            Add
          </button>
        )}
        <span className="cb-scope">
          {verb === 'do'
            ? 'proposes changes — nothing applies without your approval'
            : verb === 'add'
              ? 'files a document into this project — ingested, graphed, indexed'
              : dealScoped
                ? 'answers from this project’s brain, with citations'
                : 'answers from the knowledge base & product guide'}
        </span>
        <button type="button" className="cb-help" onClick={() => { onClose?.(); openGuide('command-bar'); }} title="How does this work?">?</button>
      </div>
      {verb === 'add' && canUpload ? (
        <AddDocumentPortal
          compact
          scope={{ org: orgId, deal: dealId }}
          deal={deal}
          participant={participant}
        />
      ) : (
      <>
      {verb === 'do' && targets.length > 1 && (
        <select
          className="cb-target"
          value={targetDocId}
          onChange={(e) => setTargetDocId(e.target.value)}
          aria-label="Document to edit"
        >
          {targets.map((d) => (
            <option key={d.id} value={d.id}>{d.title}</option>
          ))}
          <option value="@deal">All documents (route across the project)</option>
        </select>
      )}
      {thread.length > 0 && (
        <div className="cb-thread-note">
          {verb === 'do'
            ? `Instruct will use this Ask conversation (${Math.ceil(thread.length / 2)} turns) as context — edit the instruction or submit.`
            : `Follow-up — this conversation stays in this project (${Math.ceil(thread.length / 2)} turns)`}
        </div>
      )}
      {verb === 'do' && looksLikeCounselAsk(input) && (
        <div className="cb-thread-note">That reads as a question — switch to Ask (or submit anyway: Instruct will counsel, not edit).</div>
      )}
      {verb === 'do' && looksLikePastedMail(input) && (
        <div className="cb-thread-note">This looks like pasted correspondence — the agent will extract the drafting instructions from it before planning the edit (the full paste is kept on the proposal record).</div>
      )}
      <form onSubmit={submit}>
        <textarea
          ref={inputRef}
          className="cb-input"
          rows={verb === 'do' ? 4 : 2}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(e); }
          }}
          placeholder={
            verb === 'ask'
              ? (dealScoped ? 'Ask this project — follow-ups remember this thread…  (Shift+Enter for a new line)' : 'Ask anything…  (Shift+Enter for a new line)')
              : 'Tell the agent what to change in this project…  (Shift+Enter for a new line)'
          }
          disabled={!!busy}
        />
      </form>
      <div className={`cb-body${result || busy ? ' cb-body-open' : ''}`}>
        {!result && !busy && (
          <div className="cb-suggests">
            {suggestions.map((s) => (
              <button key={s} type="button" className="cb-suggest" onClick={() => setInput(s)}>
                {s}
              </button>
            ))}
          </div>
        )}
        {busy && (
          <div className="cb-busy">
            {verb === 'ask'
              ? (dealScoped ? 'consulting this project’s brain…' : 'consulting the brain…')
              : 'the agent is studying the document and drafting a proposal…'}
            {' '}<Elapsed since={busy.since} />
            {verb === 'do' && <div className="cb-busy-note">Large documents can take a minute — the proposal lands on the document’s review panel.</div>}
          </div>
        )}
        {result?.kind === 'answer' && (
          <div className="cb-answer">
            {result.question && <div className="cb-asked">You asked: {result.question}</div>}
            {result.viaInstruct && (
              <div className="cb-thread-note">Instruct was a question — answered from this project’s brain; no edit was filed.</div>
            )}
            <div className="cb-answer-text">{result.text}</div>
            {(result.sources || []).length > 0 && (
              <div className="cb-sources">
                <div className="cb-sources-head">Sources in this project</div>
                <div className="cb-cites">
                  {result.sources.map((s) => {
                    const label = s.dealPointId
                      ? (s.heading || 'Deal point')
                      : ([s.docTitle, s.heading].filter(Boolean).join(' — ') || s.source || 'source');
                    const to = s.docId
                      ? `/deals/${s.dealId || dealId}/docs/${s.docId}${s.clauseId ? `?clause=${s.clauseId}` : ''}`
                      : s.dealPointId
                        ? `/deals/${s.dealId || dealId}`
                        : null;
                    return to ? (
                      <Link key={s.n} className="cb-cite" to={to} onClick={() => onClose?.()}>
                        [{s.n}] {label}
                      </Link>
                    ) : (
                      <span key={s.n} className="cb-cite">[{s.n}] {label}</span>
                    );
                  })}
                </div>
              </div>
            )}
            {(result.citations || []).length > 0 && (
              <div className="cb-sources">
                <div className="cb-sources-head">Guide & knowledge</div>
                <div className="cb-cites">
                  {result.citations.map((c) => (
                    <span key={c.ref} className="cb-cite">[{c.ref}] {c.title}</span>
                  ))}
                </div>
              </div>
            )}
            {canInstruct && (
              <div className="cb-next">
                <button
                  type="button"
                  className="cs-btn cs-btn-sm"
                  onClick={() => {
                    setVerb('do');
                    setInput(instructionFromThread(thread, result.question));
                    setTimeout(() => inputRef.current?.focus(), 40);
                  }}
                >
                  Instruct from this conversation →
                </button>
                {result.studyDocId && (
                  <button
                    type="button"
                    className="cs-btn cs-btn-sm cs-btn-outline"
                    onClick={() => {
                      const recs = (result.recommendations || []).map((r, i) =>
                        `${i + 1}. [${r.targetHint || 'agreement'} / ${r.clauseHeading || 'clause'}] ${r.issue}` +
                        (r.suggestedLanguage ? `\n   Suggested: ${r.suggestedLanguage}` : '')
                      ).join('\n');
                      setVerb('do');
                      setInput(
                        `Incorporate the market study “${result.studyTitle || 'Market study'}” into this agreement as a precision edit. ` +
                        `Apply the recommendations. The applied version exports as a Word redline (tracked changes).\n\n${recs}`
                      );
                      setTimeout(() => inputRef.current?.focus(), 40);
                    }}
                  >
                    Incorporate study into the draft →
                  </button>
                )}
                {result.studyDocId && (
                  <Link className="cs-btn cs-btn-sm cs-btn-ghost" to={`/deals/${dealId}/docs/${result.studyDocId}`} onClick={() => onClose?.()}>
                    Open study
                  </Link>
                )}
                <Link className="cs-btn cs-btn-sm cs-btn-ghost" to={`/deals/${dealId}`} onClick={() => onClose?.()}>
                  Deal points
                </Link>
              </div>
            )}
          </div>
        )}
        {result?.kind === 'misplaced' && (
          <div className="cb-misplaced">
            {result.question && <div className="cb-asked">Instruction: {result.question}</div>}
            <div className="cb-proposal-head">
              Belongs in another document{result.belongsIn ? ` — “${result.belongsIn}”` : ''}, not “{result.docTitle}”
            </div>
            {result.text ? <div className="cb-proposal-sum">{result.text}</div> : null}
            <div className="cb-next">
              {result.belongsInDocId && (
                <button
                  type="button"
                  className="cs-btn cs-btn-sm"
                  onClick={() => {
                    setTargetDocId(result.belongsInDocId);
                    setVerb('do');
                    setInput(result.question || '');
                    setResult(null);
                    setTimeout(() => inputRef.current?.focus(), 40);
                  }}
                >
                  Instruct “{result.belongsIn}” instead →
                </button>
              )}
              <button
                type="button"
                className="cs-btn cs-btn-sm cs-btn-outline"
                onClick={() => {
                  setVerb('ask');
                  setInput(result.belongsIn
                    ? `Which agreement owns this concept, and why not “${result.docTitle}”? ${result.question || ''}`
                    : `Which agreement in this project owns: ${result.question || 'this concept'}?`);
                  setResult(null);
                  setTimeout(() => inputRef.current?.focus(), 40);
                }}
              >
                Ask which document owns this
              </button>
            </div>
          </div>
        )}
        {result?.kind === 'dealedit' && (
          <DealEditRun runPath={result.runPath} question={result.question} dealId={dealId} onClose={onClose} />
        )}
        {result?.kind === 'alreadydone' && (
          <div className="cb-proposal">
            {result.question && <div className="cb-asked">Instruction: {result.question}</div>}
            <div className="cb-proposal-head">
              Already in the current draft{result.version ? ` (v${result.version})` : ''} of “{result.docTitle}” — no edit filed
            </div>
            {(result.evidence || result.text) && (
              <div className="cb-proposal-sum">{result.evidence || result.text}</div>
            )}
          </div>
        )}
        {result?.kind === 'proposal' && (
          <div className="cb-proposal">
            {result.question && <div className="cb-asked">Instruction: {result.question}</div>}
            {result.distilled && (
              <div className="cb-thread-note">Pasted correspondence detected — the agent worked from this extracted instruction: “{result.distilled.slice(0, 300)}{result.distilled.length > 300 ? '…' : ''}”</div>
            )}
            <div className="cb-proposal-head">Proposal filed in this project — {result.opCount} change{result.opCount === 1 ? '' : 's'} awaiting review</div>
            <div className="cb-proposal-sum">{result.summary}</div>
            {(result.skipped || []).length > 0 && (
              <p className="u-xs u-muted" style={{ margin: 0 }}>
                Skipped {result.skipped.length} change{result.skipped.length === 1 ? '' : 's'} already in the draft: {result.skipped.join('; ')}
              </p>
            )}
            <p className="u-xs u-muted" style={{ margin: 0 }}>Apply on the document, then download Word (redline) for tracked changes.</p>
            <button type="button" className="cs-btn cs-btn-sm" onClick={goReview}>
              Review on “{result.docTitle}” →
            </button>
          </div>
        )}
        {result?.kind === 'error' && <div className="cb-error">⚠ {result.text}</div>}
      </div>
      </>
      )}
      <div className="cb-foot">
        <span><kbd>enter</kbd> {verb === 'add' ? 'import when a file or paste is ready' : 'submit'}</span>
        {embedded ? <span><kbd>⌘K</kbd> focus this panel</span> : (
          <>
            <span><kbd>esc</kbd> close</span>
            <span><kbd>⌘K</kbd> toggle</span>
          </>
        )}
      </div>
    </div>
  );
}

export default function CommandBar() {
  const [open, setOpen] = useState(false);
  const [initial, setInitial] = useState(null); // prefill carried by fl:cmdk detail
  const location = useLocation();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (document.getElementById('project-command')) {
          window.dispatchEvent(new CustomEvent('fl:project-cmdk'));
          setOpen(false);
        } else {
          setInitial(null);
          setOpen((o) => !o);
        }
      } else if (e.key === 'Escape') setOpen(false);
    };
    const onOpen = (e) => {
      if (document.getElementById('project-command')) {
        window.dispatchEvent(new CustomEvent('fl:project-cmdk', { detail: e?.detail || {} }));
        return;
      }
      setInitial(e?.detail?.verb || e?.detail?.instruction ? e.detail : null);
      setOpen(true);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('fl:cmdk', onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('fl:cmdk', onOpen);
    };
  }, [location.pathname]);

  if (!API || !open || document.getElementById('project-command')) return null;

  return (
    <div className="cb-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
      <CommandPanel key={initial ? JSON.stringify(initial) : 'plain'} initial={initial} onClose={() => setOpen(false)} />
    </div>
  );
}
